import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { VmConnectionTemplateService } from '../../../../services/entity-services/vm-connection-template.service';
import {
  VmConnectionTemplateDetail,
  VmConnectionTemplateSummary,
} from '../../../../interfaces/vm-connection-templates/vm-connection-template.interface';
import { Log } from '../../../../services/logger.service';

/**
 * Sentinel summary entry that represents "no template — use the global defaults".
 *
 * Picking this entry from the autocomplete is the canonical way to detach an existing
 * attachment; we treat it as a real selectable option (unlike a separate "Detach" button)
 * so the row stays compact and the action lives next to all the other choices.
 */
const NONE_SENTINEL_ID = '__none__';

/**
 * Generic attach control for the VM Connection Template aggregate. Renders a
 * `mat-autocomplete` over the global template list with two icon-only affordances —
 * view-details and create-new — that share the row with the picker.
 *
 * Selecting the sentinel "— None (use global defaults) —" entry from the autocomplete
 * detaches the current attachment; there is no separate detach button.
 *
 * Reused identically by the per-class attach panel (Settings → VM Template) and the
 * per-resource attach panel (each org-template card after provisioning), so the two
 * surfaces stay byte-for-byte identical.
 */
@Component({
  selector: 'app-vm-connection-template-attach',
  templateUrl: './vm-connection-template-attach.component.html',
  styleUrls: ['./vm-connection-template-attach.component.css'],
})
export class VmConnectionTemplateAttachComponent implements OnInit, OnChanges {
  private readonly logger = Log.create(VmConnectionTemplateAttachComponent);

  /** Currently-attached template id, or `null` when nothing is attached. */
  @Input() currentId: string | null = null;

  /** When `true` the controls are disabled (e.g. a save is in flight). */
  @Input() disabled = false;

  /** Optional sub-heading rendered under the section title (e.g. "for this class"). */
  @Input() contextLabel: string | null = null;

  /**
   * Emitted whenever the user picks a different template id (or the "— None —" sentinel,
   * which emits `null`). The host is responsible for persisting the choice — this
   * component does not call any save endpoint itself.
   */
  @Output() change = new EventEmitter<string | null>();

  /** Available templates fetched from the global list endpoint. */
  templates: VmConnectionTemplateSummary[] = [];
  loadingList = false;
  loadError: string | null = null;

  /** Detail of the currently-attached template — fetched on demand for the view drawer. */
  viewedDetail: VmConnectionTemplateDetail | null = null;
  isLoadingDetail = false;
  showDetailDrawer = false;

  /** Toggles the in-place vertical create panel. */
  showCreatePanel = false;

  /**
   * Form control backing the autocomplete input. Holds either a
   * {@link VmConnectionTemplateSummary}, the {@link NONE_SENTINEL_ID} marker, or a raw
   * search string (while the user is typing before picking from the panel).
   */
  readonly pickerControl = new FormControl<VmConnectionTemplateSummary | string | null>(null);

  /** Live-filtered template list for the autocomplete panel. */
  filteredTemplates$: Observable<VmConnectionTemplateSummary[]> | null = null;

  /** Sentinel exposed to the template so it can render a recognisable "None" entry. */
  readonly NONE_SENTINEL = NONE_SENTINEL_ID;

  constructor(private readonly templateService: VmConnectionTemplateService) {}

  ngOnInit(): void {
    this.filteredTemplates$ = this.pickerControl.valueChanges.pipe(
      startWith<VmConnectionTemplateSummary | string | null>(this.pickerControl.value),
      map(value => this.filterTemplates(value)),
    );
    this.loadTemplates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Keep the input box in sync when the host updates the attached id (e.g. after a
    // successful save, or when navigating to a class with an existing attachment).
    if (changes['currentId']) {
      this.syncPickerToCurrentId();
    }
    if (changes['disabled']) {
      changes['disabled'].currentValue ? this.pickerControl.disable({ emitEvent: false })
                                       : this.pickerControl.enable({ emitEvent: false });
    }
  }

  /** Fetches the global template list — refreshed after each create. */
  loadTemplates(): void {
    this.loadingList = true;
    this.loadError = null;
    this.templateService.getAll().subscribe({
      next: list => {
        this.templates = list ?? [];
        this.loadingList = false;
        this.syncPickerToCurrentId();
      },
      error: err => {
        this.logger.e('Failed to load VM connection templates', err);
        this.loadError = 'Failed to load templates.';
        this.loadingList = false;
      },
    });
  }

  /**
   * Filters the loaded templates by the current input value. When the input holds a
   * full {@link VmConnectionTemplateSummary} object (post-pick) every template is
   * shown so the user can easily switch.
   */
  private filterTemplates(value: VmConnectionTemplateSummary | string | null): VmConnectionTemplateSummary[] {
    if (!value || typeof value !== 'string') return this.templates.slice();
    const needle = value.trim().toLowerCase();
    if (needle.length === 0) return this.templates.slice();
    return this.templates.filter(t =>
      (t.templateName ?? '').toLowerCase().includes(needle) ||
      (t.description  ?? '').toLowerCase().includes(needle));
  }

  /** Display function for the autocomplete input — shows the friendly template name. */
  displayTemplate = (value: VmConnectionTemplateSummary | string | null): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.templateName ?? '';
  };

  /**
   * Triggered by `(optionSelected)` on the autocomplete. Translates the selected option
   * into a stable id (or `null` for the sentinel) and emits {@link change} when it's
   * different from the current attachment.
   */
  onOptionSelected(value: VmConnectionTemplateSummary | string | null): void {
    let nextId: string | null = null;
    if (typeof value === 'string') {
      nextId = value === NONE_SENTINEL_ID ? null : null;
    } else if (value && typeof value === 'object') {
      nextId = (value as VmConnectionTemplateSummary).id ?? null;
    }

    // Re-pick of the same value is a no-op — avoids triggering a redundant save.
    if (nextId === this.currentId) {
      // Snap the input back to the canonical display so partial typing doesn't linger.
      this.syncPickerToCurrentId();
      return;
    }

    this.change.emit(nextId);
  }

  /**
   * Returns the summary projection of the currently-attached template if it's present
   * in the loaded list, else `null` (e.g. before the list arrives).
   */
  get currentSummary(): VmConnectionTemplateSummary | null {
    if (!this.currentId) return null;
    return this.templates.find(t => t.id === this.currentId) ?? null;
  }

  /** Push the live attachment value back into the autocomplete control. */
  private syncPickerToCurrentId(): void {
    const summary = this.currentSummary;
    this.pickerControl.setValue(summary ?? '', { emitEvent: false });
  }

  /** Opens the side drawer rendering the read-only details view. */
  openViewDrawer(): void {
    if (!this.currentId) return;
    this.isLoadingDetail = true;
    this.viewedDetail = null;
    this.showDetailDrawer = true;
    this.templateService.getById(this.currentId).subscribe({
      next: detail => {
        this.viewedDetail = detail;
        this.isLoadingDetail = false;
      },
      error: err => {
        this.logger.e('Failed to load VM connection template detail', err);
        this.isLoadingDetail = false;
        this.showDetailDrawer = false;
      },
    });
  }

  closeDrawer(): void {
    this.showDetailDrawer = false;
    this.viewedDetail = null;
  }

  /** Opens the inline vertical create panel. */
  openCreatePanel(): void {
    if (this.disabled) return;
    this.showCreatePanel = true;
  }

  closeCreatePanel(): void {
    this.showCreatePanel = false;
  }

  /**
   * Called by the create panel once the new template is persisted. Re-fetches the dropdown
   * list (so the new entry is selectable) and emits {@link change} so the host wires the
   * fresh id into its attachment side.
   */
  onTemplateCreated(detail: VmConnectionTemplateDetail): void {
    this.showCreatePanel = false;
    this.loadTemplates();
    this.change.emit(detail.id);
  }

  /**
   * Called when the create panel's duplicate banner CTA is clicked — picks the existing
   * template instead of creating a new one. Same behaviour as a regular pick.
   */
  onPickExistingDuplicate(existing: VmConnectionTemplateSummary): void {
    this.showCreatePanel = false;
    if (existing.id === this.currentId) return;
    this.change.emit(existing.id);
  }
}
